/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";

const Starfield: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const hoverTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Scene, Camera, Renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.8,
      1000
    );
    camera.position.z = 3;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // Store initial positions for stars (Points)
    const starCount = 1000;
    const starGeometry = new THREE.BufferGeometry();
    const starPositions = new Float32Array(starCount * 3);
    const starInitialPositions = new Float32Array(starCount * 3);

    for (let i = 0; i < starCount; i++) {
      const x = (Math.random() - 0.5) * 10;
      const y = (Math.random() - 0.5) * 10;
      const z = (Math.random() - 0.5) * 10;
      starPositions.set([x, y, z], i * 3);
      starInitialPositions.set([x, y, z], i * 3);
    }
    starGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(starPositions, 3)
    );

    // Instanced spheres
    const sphereGeometry = new THREE.SphereGeometry(0.02, 8, 8);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0x396661 });
    const meshCount = 500;
    const instancedMesh = new THREE.InstancedMesh(
      sphereGeometry,
      sphereMaterial,
      meshCount
    );

    // Store initial positions for spheres
    const spherePositions = new Float32Array(meshCount * 3);
    // Velocity per sphere (x, y, z)
    const sphereVelocity = new Float32Array(meshCount * 3);

    for (let i = 0; i < meshCount * 3; i++) {
      sphereVelocity[i] = (Math.random() - 0.5) * 0.002;
    }

    for (let i = 0; i < meshCount; i++) {
      const ix = i * 3;
      spherePositions[ix] = (Math.random() - 0.5) * 10;
      spherePositions[ix + 1] = (Math.random() - 0.5) * 10;
      spherePositions[ix + 2] = (Math.random() - 0.5) * 10;

      const matrix = new THREE.Matrix4().setPosition(
        spherePositions[ix],
        spherePositions[ix + 1],
        spherePositions[ix + 2]
      );
      instancedMesh.setMatrixAt(i, matrix);
    }

    scene.add(instancedMesh);

    // Load circular texture for stars
    const textureLoader = new THREE.TextureLoader();
    const starTexture = textureLoader.load("/circle.png");

    const starMaterial = new THREE.PointsMaterial({
      map: starTexture,
      size: 0.05,
      transparent: true,
      depthWrite: true,
      fog: true,
    });

    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    scene.fog = new THREE.FogExp2(0x000000, 0.15);

    // Mouse interaction
    let mouseX = 0;
    let mouseY = 0;

    const resetHoverTimer = () => {
      // Clear any existing timer
      if (hoverTimeoutRef.current !== undefined) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
      // After 1 second of no movement, hovered = false
      hoverTimeoutRef.current = window.setTimeout(() => {
        setHovered(false);
      }, 200);
    };

    const onMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX / window.innerWidth - 0.5;
      mouseY = e.clientY / window.innerHeight - 0.5;
      setHovered(true);
      resetHoverTimer();
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseout", () => {
      resetHoverTimer();
    });

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", onResize);

    let time = 0;
    const matrix = new THREE.Matrix4();

    const animate = () => {
      requestAnimationFrame(animate);
      time += 0.01;

      // // Rotate scene based on mouse
      // scene.rotation.y = mouseX * 0.6;
      // scene.rotation.x = mouseY * 0.6;

      if (!hovered) {
        // Apply gentle sinusoidal drifting when not hovered

        // Update star positions
        const posAttr = starGeometry.getAttribute(
          "position"
        ) as THREE.BufferAttribute;
        const posArray = posAttr.array as Float32Array;

        for (let i = 0; i < starCount; i++) {
          const ix = i * 3,
            iy = ix + 1,
            iz = ix + 2;

          const x0 = starInitialPositions[ix];
          const y0 = starInitialPositions[iy];
          const z0 = starInitialPositions[iz];

          // Small sinusoidal offsets
          posArray[ix] = x0 + Math.sin(time + i * 0.1) * 0.02;
          posArray[iy] = y0 + Math.sin(time + i * 0.1) * 0.02;
          posArray[iz] = z0 + Math.cos(time + i * 0.1) * 0.02;
        }
        posAttr.needsUpdate = true;

        // Update sphere positions
        // Free-floating velocity-based motion
        for (let i = 0; i < meshCount; i++) {
          const p = i * 3;

          // Small random acceleration
          sphereVelocity[p] += (Math.random() - 0.5) * 0.00025;
          sphereVelocity[p + 1] += (Math.random() - 0.5) * 0.00025;
          sphereVelocity[p + 2] += (Math.random() - 0.5) * 0.00025;

          // Damping (inertia)
          sphereVelocity[p] *= 0.985;
          sphereVelocity[p + 1] *= 0.985;
          sphereVelocity[p + 2] *= 0.985;

          // Integrate velocity into position
          spherePositions[p] += sphereVelocity[p];
          spherePositions[p + 1] += sphereVelocity[p + 1];
          spherePositions[p + 2] += sphereVelocity[p + 2];

          matrix.setPosition(
            spherePositions[p],
            spherePositions[p + 1],
            spherePositions[p + 2]
          );

          instancedMesh.setMatrixAt(i, matrix);
        }

        instancedMesh.instanceMatrix.needsUpdate = true;
      } else {
        // If hovered is true, we do not apply the drifting,
        // so the objects remain at their initial positions (except for scene rotation).
      }

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      if (hoverTimeoutRef.current !== undefined) {
        clearTimeout(hoverTimeoutRef.current);
      }
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
      if (mount && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={mountRef}
      className="absolute top-0 left-0 w-full h-full -z-10
               [mask-image:linear-gradient(to_bottom,black_10%,transparent_80%)]
               [-webkit-mask-image:linear-gradient(to_bottom,black_10%,transparent_80%)]"
    />
  );
};

export default Starfield;
